import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../auth';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Building2, FileText, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

export default function PendingApprovalsPage() {
  const [pending, setPending] = useState([]);
  const [viewLicense, setViewLicense] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    const r = await axios.get(`${API}/companies/pending`);
    setPending(r.data);
  };

  const approve = async (id) => {
    try {
      await axios.post(`${API}/companies/${id}/approve`);
      toast.success('Отмечено как просмотрено');
      fetchData();
    } catch (err) { toast.error('Ошибка'); }
  };

  const reject = async (id) => {
    if (!window.confirm('Удалить эту компанию навсегда?')) return;
    try {
      await axios.post(`${API}/companies/${id}/reject`);
      toast.success('Компания удалена');
      fetchData();
    } catch (err) { toast.error('Ошибка'); }
  };

  return (
    <div className="space-y-6" data-testid="pending-approvals-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Новые регистрации</h1>
        <p className="text-gray-500 mt-1">Компании, зарегистрировавшиеся самостоятельно ({pending.length})</p>
      </div>

      {pending.length === 0 ? (
        <Card className="p-12 text-center">
          <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Новых регистраций нет</p>
          <p className="text-xs text-gray-400 mt-1">
            Клиенты регистрируются на: <span className="font-mono">{window.location.origin}/register</span>
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {pending.map(c => (
            <Card key={c.id} className="p-5" data-testid={`pending-${c.client_code}`}>
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-mono text-xs text-gray-500">{c.client_code}</span>
                    <span className="px-2 py-0.5 text-xs bg-emerald-100 text-emerald-800 rounded">✓ Активен</span>
                    <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-800 rounded">🆕 Новая</span>
                  </div>
                  <h3 className="text-lg font-semibold">{c.company_name}</h3>
                  <div className="text-sm text-gray-600 mt-2 space-y-1">
                    <p>👤 {c.contact_person}</p>
                    <p>📞 {c.phone}</p>
                    <p>✉️ {c.email}</p>
                    {c.delivery_locations?.[0] && (
                      <p>📍 {c.delivery_locations[0].address}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-2">Зарегистрирована: {new Date(c.created_at).toLocaleString('ru-RU')}</p>
                  </div>
                </div>
                <div className="flex flex-col gap-2 md:w-56">
                  {c.business_license && (
                    <Button variant="outline" size="sm" onClick={() => setViewLicense(c.business_license)} data-testid={`view-license-${c.client_code}`}>
                      <Eye className="h-4 w-4 mr-2" /> Документ
                    </Button>
                  )}
                  <Button onClick={() => approve(c.id)} className="cff-button-primary" data-testid={`approve-${c.client_code}`}>
                    <CheckCircle className="h-4 w-4 mr-2" /> Отметить просмотренным
                  </Button>
                  <Button variant="outline" onClick={() => reject(c.id)} className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`reject-${c.client_code}`}>
                    <XCircle className="h-4 w-4 mr-2" /> Удалить
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!viewLicense} onOpenChange={(o) => !o && setViewLicense(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Свидетельство о регистрации бизнеса</DialogTitle></DialogHeader>
          {viewLicense && (
            viewLicense.startsWith('data:application/pdf') ? (
              <iframe src={viewLicense} className="w-full h-[70vh]" />
            ) : (
              <img src={viewLicense} alt="License" className="max-h-[70vh] mx-auto" />
            )
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
